import { AlertTriangle, Scale, Ban, FileWarning, Gavel, DollarSign, ShieldAlert, AlertOctagon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Reveal, RevealStagger } from "@/components/Reveal";

const problematicas = [
  {
    title: "Sin Permiso de Autoconsumo CNE",
    icon: FileWarning,
    description: "Operar una estación de combustible para autoconsumo sin el permiso correspondiente de la Comisión Nacional de Energía constituye una infracción grave.",
    consecuencias: [
      {
        tipo: "Multas Administrativas",
        icon: DollarSign,
        detalle: "Multas que van desde 30,000 hasta 3,000,000 de UMAS (Unidad de Medida y Actualización), equivalente a aproximadamente $3,200,000 a $320,000,000 MXN dependiendo de la gravedad."
      },
      {
        tipo: "Clausura de Instalaciones",
        icon: Ban,
        detalle: "La autoridad puede ordenar la clausura temporal o definitiva de las instalaciones donde se almacena o despacha combustible sin permiso."
      },
      {
        tipo: "Decomiso de Producto",
        icon: AlertOctagon,
        detalle: "El combustible almacenado puede ser decomisado por las autoridades, generando pérdidas económicas significativas."
      },
      {
        tipo: "Responsabilidad Legal",
        icon: Gavel,
        detalle: "Operar sin permiso de la CNE puede derivar en clausura definitiva de instalaciones, inhabilitación para obtener permisos futuros y responsabilidad civil por daños a terceros."
      }
    ]
  },
  {
    title: "Sin Controles Volumétricos SAT",
    icon: ShieldAlert,
    description: "No contar con sistemas de controles volumétricos certificados representa un incumplimiento fiscal grave ante el Servicio de Administración Tributaria.",
    consecuencias: [
      {
        tipo: "Multas Fiscales",
        icon: DollarSign,
        detalle: "Multas de $17,000 a $170,000 MXN por cada mes de incumplimiento en la presentación de reportes volumétricos."
      },
      {
        tipo: "Suspensión del CSD",
        icon: Ban,
        detalle: "El SAT puede suspender el Certificado de Sello Digital (CSD), impidiendo la emisión de facturas y paralizando la operación comercial."
      },
      {
        tipo: "Presunción de Contrabando",
        icon: AlertOctagon,
        detalle: "La falta de controles volumétricos puede generar presunción de contrabando de combustible, con consecuencias penales."
      },
      {
        tipo: "Auditorías y Créditos Fiscales",
        icon: Scale,
        detalle: "El SAT puede iniciar auditorías que deriven en créditos fiscales por evasión de impuestos (IEPS, IVA) con recargos y actualizaciones."
      }
    ]
  }
];

export default function ProblematicaSection() {
  return (
    <section id="problematica" data-testid="problematica-section" className="py-24 bg-gradient-to-b from-[#fff8f0] to-[#f5f0e8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="text-center mb-16">
            <p className="text-[#c9432f] text-xs uppercase tracking-[0.3em] mb-4 font-semibold flex items-center justify-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Información Importante
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1a1a2e] mb-6">
              Problemática de operar sin permisos
            </h2>
            <p className="text-[#4a4540] max-w-2xl mx-auto text-lg">
              Conoce las consecuencias legales, administrativas y penales de no contar con los permisos y controles requeridos por la ley.
            </p>
          </div>
        </Reveal>

        <RevealStagger staggerDelay={0.15}>
          <div className="space-y-12">
            {problematicas.map((problema, idx) => {
              const IconComponent = problema.icon;
              return (
                <Reveal key={idx}>
                  <Card className="border-none shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-[#c9432f] to-[#a83828] p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white/20 rounded-xl">
                          <IconComponent className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{problema.title}</h3>
                          <p className="text-white/80 text-sm mt-1">{problema.description}</p>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6 bg-white">
                      <p className="text-xs font-semibold text-[#c9432f] uppercase tracking-wider mb-4">
                        Consecuencias y Sanciones
                      </p>
                      <div className="grid md:grid-cols-2 gap-4">
                        {problema.consecuencias.map((cons, cidx) => {
                          const ConsIcon = cons.icon;
                          return (
                            <div 
                              key={cidx} 
                              className="p-4 bg-[#faf8f4] rounded-xl border border-[#e8e4dc] hover:border-[#c9432f]/30 transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-[#c9432f]/10 rounded-lg flex-shrink-0">
                                  <ConsIcon className="h-5 w-5 text-[#c9432f]" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-[#1a1a2e] text-sm mb-1">{cons.tipo}</h4>
                                  <p className="text-[#4a4540] text-sm leading-relaxed">{cons.detalle}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </RevealStagger>

        {/* Call to Action */}
        <Reveal>
          <div className="mt-16 text-center">
            <div className="inline-block bg-[#1a1a2e] text-white rounded-2xl p-8 max-w-2xl">
              <AlertTriangle className="h-10 w-10 mx-auto mb-4 text-[#f5c842]" />
              <h3 className="text-2xl font-bold mb-4 uppercase tracking-wide">NO ARRIESGUES TU NEGOCIO</h3>
              <p className="text-white/90 mb-6 text-lg">
                La regularización es más económica que enfrentar las consecuencias. Solicita un diagnóstico gratuito y conoce tu situación actual.
              </p>
              <p className="text-sm text-white/60">
                En PETROCONTROL te ayudamos a obtener todos los permisos necesarios 
                y a implementar los controles volumétricos para operar de manera legal.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
